import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Music2, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getMyArtist, linkMyArtist, managersMe } from "@/lib/afmApi";
import { ArtistPicker } from "@/components/ArtistPicker";
import { LogoMark } from "@/components/Logo";

const ADMIN_ROLES = ["admin", "distribution_admin", "superadmin", "super_admin", "staff", "operator", "ar"];

// Paso obligatorio para cuentas de ARTISTA: vincular su perfil (Spotify / Apple / nuevo) antes de usar la web.
// Admins y cuentas de manager/sello no lo ven (trabajan con su lista de artistas).
export default function ArtistLinkGate({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState("checking"); // checking | ok | needs
  const [pick, setPick] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("search");

  const exempt = !user || ADMIN_ROLES.includes(user.role) || user.super_admin || user.distribution_admin;

  useEffect(() => {
    if (exempt) { setState("ok"); return; }
    let alive = true;
    Promise.all([getMyArtist(user.email), managersMe().catch(() => null)]).then(([a, m]) => {
      if (!alive) return;
      setState(a?.name || m?.manager ? "ok" : "needs");
    }).catch(() => alive && setState("ok")); // si falla la consulta, no bloquear (el panel lo valida al enviar)
    return () => { alive = false; };
  }, [user?.email, exempt]); // eslint-disable-line react-hooks/exhaustive-deps

  const link = async (a) => {
    setBusy(true);
    try {
      await linkMyArtist({ email: user.email, name: a.name, spotify_id: a.spotify_id, spotify_url: a.spotify_url, apple_id: a.apple_id, apple_url: a.apple_url, image: a.image });
      toast.success(`Listo: tu cuenta es de ${a.name}`);
      setState("ok");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo vincular");
      setPick([]);
    } finally { setBusy(false); }
  };

  if (state === "ok") return children;
  if (state === "checking") return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-black" /></div>;

  return (
    <div className="flex min-h-[70vh] items-center justify-center p-4" data-testid="artist-link-gate">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#270A0A]"><LogoMark className="h-7 w-7 text-white" /></div>
          <div>
            <h1 className="font-serif text-2xl font-bold text-black">Vincula tu perfil de artista</h1>
            <p className="text-sm text-slate-500">Un solo paso antes de empezar.</p>
          </div>
        </div>
        <p className="mb-5 text-sm text-slate-600">
          Tu cuenta de Always Fresh es de <strong>un solo artista</strong>. Toda la música que distribuyas irá a <strong>tu perfil</strong> en las tiendas.
          Puedes sumar colaboradores en cada lanzamiento, pero tú siempre serás el artista principal.
        </p>

        {mode === "search" ? (
          <>
            <ArtistPicker single value={pick} onChange={(arr) => { setPick(arr); if (arr[0]) link(arr[0]); }} testId="gate-artist" userId={user.id} ph="Busca tu nombre artístico en Spotify o Apple Music…" />
            {busy && <p className="mt-2 text-xs text-slate-500">Vinculando…</p>}
            <button onClick={() => setMode("new")} className="mt-5 flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-black"><UserPlus className="h-4 w-4" /> Soy nuevo: aún no tengo perfil en Spotify</button>
          </>
        ) : (
          <>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-600">Tu nombre artístico</label>
            <input value={newName} onChange={(e) => setNewName(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-black" placeholder="Como quieres aparecer en las tiendas" />
            <p className="mt-2 text-[11px] text-slate-500">Revisa bien cómo lo escribes: así se creará tu perfil en Spotify, Apple Music y demás tiendas con tu primer lanzamiento.</p>
            <div className="mt-4 flex gap-2">
              <button disabled={busy || newName.trim().length < 2} onClick={() => link({ name: newName.trim() })} className="flex items-center gap-1.5 rounded-lg bg-black px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-40"><Music2 className="h-4 w-4" /> {busy ? "Creando…" : "Crear mi perfil"}</button>
              <button onClick={() => setMode("search")} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm hover:border-black">Volver a buscar</button>
            </div>
          </>
        )}
        <p className="mt-6 text-[11px] text-slate-400">¿Eres manager o sello y manejas varios artistas? Escríbenos a soporte y te creamos una cuenta de sello.</p>
      </div>
    </div>
  );
}
